import { FC, useEffect, useMemo } from "react";
import { t } from "i18next";
import { round } from "lodash";

import { AMENITY_CONSTANTS } from "@constants/amenities";
import { vatHandlingType } from "@enums/hooks.enum";
import { PriceSummaryProps } from "@models/services/pricing";
import { formatPrice } from "@utils/common.utils";

import { PriceRow } from "../PriceRow/PriceRow";

export const PriceSummary: FC<PriceSummaryProps> = ({
  amenities = [],
  basePrice,
  bookingEditData,
  currency,
  guestPrice,
  isEditBooking,
  isFromDrawer = false,
  onGrandTotalChange,
  priceLoader = false,
  totalPriceWithTax,
  vatAmount = 0,
  vatDetails,
}) => {
  const isVatInclusive = useMemo(
    () =>
      vatDetails?.hasVat &&
      vatDetails?.vatHandlingType === vatHandlingType.INCLUSIVE,
    [vatDetails?.hasVat, vatDetails?.vatHandlingType]
  );
  const hasVat = useMemo(() => vatDetails?.hasVat, [vatDetails?.hasVat]);
  const vatPercentage = Number(vatDetails?.vatPercentage || 0);
  const showVat = vatDetails?.hasVat && vatPercentage > 0;

  const firstRowClass = isFromDrawer ? "col-7" : "col-9";
  const secondRowClass = isFromDrawer ? "col-5 text-end" : "col-3 text-end";

  const totalAmenityVat = useMemo(() => {
    if (!hasVat) return 0;
    return amenities.reduce((acc, a) => acc + (Number(a.vatAmount) || 0), 0);
  }, [amenities, hasVat]);
  const totalAmenityPrice = useMemo(
    () => amenities.reduce((acc, a) => acc + (a.unitPriceTotal || 0), 0),
    [amenities]
  );

  const totalVatSum = round(hasVat ? (vatAmount || 0) + totalAmenityVat : 0, 2);

  const totalResourcePrice = totalPriceWithTax || 0;

  const grandTotal = isVatInclusive
    ? totalResourcePrice + totalAmenityPrice
    : totalResourcePrice - (vatAmount || 0) + totalAmenityPrice + totalVatSum;

  useEffect(() => {
    if (onGrandTotalChange) {
      onGrandTotalChange(grandTotal);
    }
  }, [grandTotal, onGrandTotalChange]);

  const amountToPaid = useMemo(() => {
    if (!isEditBooking || bookingEditData?.totalPrice == null) return 0;
    return round(grandTotal, 2) - round(bookingEditData.totalPrice, 2);
  }, [isEditBooking, bookingEditData?.totalPrice, grandTotal]);

  const activeVatLabel = isVatInclusive
    ? t("inclVat", { rate: vatPercentage })
    : t("exclVatShort");

  const vatRowLabel = showVat ? activeVatLabel : "";

  const totalActiveVatLabel = isVatInclusive
    ? `${t("inclusive")} ${vatPercentage}%`
    : t("exclusive");

  const totalVatLabel = showVat ? `(${totalActiveVatLabel})` : "";

  const isEditingWithBalance = isEditBooking && amountToPaid !== 0;
  const hasValidEditData = bookingEditData?.totalPrice !== undefined;
  const showEditBookingDetails = isEditingWithBalance && hasValidEditData;

  const editWrapperClass = isEditingWithBalance
    ? "fg-total-price-edit-wrapper"
    : "";
  const pricePaidWrapperClass = showEditBookingDetails
    ? "fg-price-paid-details-wrapper"
    : "";
  const payEditWrapperClass = isEditingWithBalance
    ? "fg-total-price-to-pay-edit-wrapper"
    : "";

  const balanceToPayLabel =
    amountToPaid < 0
      ? t("bookingEdit.AmountToReduced")
      : t("bookingEdit.AmountToPaid");
  return (
    <div
      className={`fg-price-summary-container gutter-less ${
        priceLoader ? "fg-loading-state" : ""
      }`}
    >
      <div className="total-prize-section">
        {/* BOOKING SECTION */}
        <h6 className="fg-summary-section-header">{t("BOOKING")}</h6>
        <PriceRow
          label={t("priceForBooking")}
          secondaryLabel={vatRowLabel}
          amount={formatPrice(basePrice, currency)}
          firstRowClass={firstRowClass}
          secondRowClass={secondRowClass}
        />

        {/* PARTICIPANTS SECTION */}
        {guestPrice > 0 && (
          <PriceRow
            label={t("GuestPrice")}
            secondaryLabel={vatRowLabel}
            amount={formatPrice(guestPrice, currency)}
            firstRowClass={firstRowClass}
            secondRowClass={secondRowClass}
          />
        )}

        {/* AMENITIES SECTION */}
        {amenities.length > AMENITY_CONSTANTS.DEFAULT_AMENITY_COUNT && (
          <>
            <h6 className="fg-summary-section-header mt-3">{t("AMENITIES")}</h6>
            {amenities.map(amenity => {
              const activeAmenityVatLabel = isVatInclusive
                ? t("inclVat", { rate: amenity.vatPercentage })
                : t("exclVatShort");

              const amenityVatLabel = showVat ? activeAmenityVatLabel : "";

              return (
                <PriceRow
                  key={amenity.id}
                  label={`${amenity.title} × ${amenity.count}`}
                  secondaryLabel={amenityVatLabel}
                  amount={formatPrice(amenity.unitPriceTotal, currency)}
                  firstRowClass={firstRowClass}
                  secondRowClass={secondRowClass}
                />
              );
            })}
          </>
        )}

        <hr className="fg-summary-divider" />

        {/* VAT SUMMARY ROW */}
        {showVat && (
          <PriceRow
            label={`${t("VAT")} ${vatPercentage}%`}
            amount={formatPrice(totalVatSum, currency)}
            labelClassName="fg-unit-vat-label"
            showMarginTop
            firstRowClass={firstRowClass}
            secondRowClass={secondRowClass}
          />
        )}

        <hr className="fg-summary-divider" />

        {/* EDIT BOOKING LOGIC */}
        <div className={editWrapperClass}>
          <div className={pricePaidWrapperClass}>
            {showEditBookingDetails && (
              <PriceRow
                label={t("bookingEdit.AmountPaid")}
                amount={formatPrice(bookingEditData?.totalPrice, currency)}
                labelClassName="fg-unit-amount-paid-label "
                firstRowClass={firstRowClass}
                secondRowClass={secondRowClass}
              />
            )}

            {/* TOTAL PRICE ROW */}
            <PriceRow
              label={t("totalPrice")}
              secondaryLabel={totalVatLabel}
              amount={formatPrice(grandTotal, currency)}
              labelClassName="fg-grand-total-label"
              amountClass="fg-grand-total-amount"
              firstRowClass={firstRowClass}
              secondRowClass={secondRowClass}
            />
          </div>

          <div className={payEditWrapperClass}>
            {isEditingWithBalance && (
              <PriceRow
                label={balanceToPayLabel}
                amount={formatPrice(Math.abs(amountToPaid), currency)}
                labelClassName="fg-unit-balance-to-paid-label"
                firstRowClass={firstRowClass}
                secondRowClass={secondRowClass}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
